<template>
    <div class="add-accommodation">
        <div class="text-right">
            <button class="button" @click="toggleModal">
                {{ $t('portals.edit.selectTypes') }}
            </button>
        </div>

        <div class="modal is-active" v-if="showModal">
            <div class="modal-background"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">
                        {{ $t('portals.form.create') }}
                    </p>
                    <button class="delete" @click="toggleModal"/>
                </header>
                <section class="modal-card-body">
                    <template v-for="field in form">
                        <div
                            :key="form.name"
                            v-if="!field.isHidden"
                            class="column"
                        >
                            <label
                                :for="field.key"
                                class="label"
                            >
                                {{ field.name }}
                            </label>

                            <div class="portals__form-field">
                                <template v-if="field.type === 'select'">
                                    <multiselect
                                        :id="field.key"
                                        :name="field.key"
                                        v-model="field.value"
                                        :options="field.options()"
                                        :multiple="false"
                                        :searchable="false"
                                        :hide-selected="true"
                                        :placeholder="field.name"
                                        :close-on-select="true"
                                        selectLabel=""
                                        label="name"
                                        track-by="name"
                                    />
                                </template>
                                <template v-else-if="field.type === 'date'">
                                    <datepicker
                                        class="time-period-input"
                                        :placeholder="$t('generic.calendar.placeholder')"
                                        :config="pickerConfig"
                                        v-model="field.value"
                                    >
                                        <a class="button" data-toggle><i class="fa fa-calendar"></i></a>
                                    </datepicker>
                                </template>
                                <template v-else>
                                    <input
                                        :id="field.key"
                                        class="input"
                                        v-model="field.value"
                                        :placeholder="field.name"
                                        :type="field.type"
                                    >
                                </template>
                            </div>
                        </div>
                    </template>
                </section>
                <footer class="modal-card-foot">
                    <div class="block ml-auto">
                        <button class="button is-link" @click.prevent="toggleModal">
                            {{ $t('generic.cancel') }}
                        </button>
                        <button
                            class="button is-primary"
                            :class="{'is-disabled': isFormInvalid }"
                            @click.prevent="savePortal"
                        >
                            {{ $t('generic.save') }}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import Datepicker from 'vue-bulma-datepicker';
import { AppModel } from '../../../models';
import { Api } from '../../../api';
import { EventBus } from '../../../utils';

export default {
    name: 'addAccommodation',
    components: { Datepicker, Multiselect },
    props: {
        campsites: Array,
        portal: Object,
    },
    data() {
        return {
            showModal: false,
            pickerConfig: AppModel.datepickerOptions,
            form: {
                portal: {
                    key: 'portal_id',
                    name: this.$t('portals.form.portal'),
                    value: this.getSelectedPortal(),
                    valueKey: 'id',
                    type: 'select',
                    options: this.getPortals,
                    isHidden: true,
                },
                campsite: {
                    key: 'campsite_id',
                    name: this.$t('portals.form.campsite'),
                    valueKey: 'id',
                    value: null,
                    type: 'select',
                    options: this.getCampsites,
                    isHidden: false,
                },
                valid_from: {
                    key: 'valid_from',
                    name: this.$t('portals.form.valid_from'),
                    value: null,
                    type: 'date',
                    showDatePicker: false,
                    isHidden: true,
                },
                valid_to: {
                    key: 'valid_to',
                    name: this.$t('portals.form.valid_to'),
                    value: null,
                    type: 'date',
                    showDatePicker: false,
                    isHidden: true,
                },
                allotment: {
                    key: 'allotment',
                    name: this.$t('portals.form.amount'),
                    value: null,
                    type: 'number',
                    isHidden: true,
                },
            },
        };
    },
    watch: {
        form: {
            handler(form) {
                if(!form.portal.value) {
                    return null;
                }

                const portal = form.portal.value;

                this.form.valid_from.isHidden = !portal.allow_periods;
                this.form.valid_to.isHidden = !portal.allow_periods;
                this.form.allotment.isHidden = !(this.form.campsite.value && portal.allow_allotments);

                this.form.allotment.value = Math.abs(this.form.allotment.value);
            },
            deep: true,
        },
    },
    computed: {
        isFormInvalid() {
            return this.form.portal.value === '' || this.form.portal.value === null;
        },
    },
    methods: {
        toggleModal() {
            this.showModal = !this.showModal;
        },
        getSelectedPortal() {
            return this.getPortals().find((portal) => portal.id === this.portal.id);
        },
        getPortals() {
            return AppModel.portals;
        },
        getCampsites() {
            const emptyCampsite = {
                id: null,
                value: null,
                name: this.$t('portals.form.noSelection'),
            };
            const arr = [];

            arr.push(emptyCampsite);

            let keys = Object.keys(AppModel.campsiteTypes);
            keys.forEach(key => {
                let e = {id: -1,
                    value: key,
                    name: '-- ' + this.$t('generic.campsite_types.' + key + '.portal_select_name') + ' --'};
                arr.push(e);
            });

            arr.push(...this.campsites);

            return arr;
        },
        async savePortal() {
            const data = {
                campground_slug: AppModel.activeCampground.slug,
                // portal_id: this.form.portal.value[ this.form.portal.valueKey ],
                portal_id: this.portal.id,
                campsite_id: this.form.campsite.value ? this.form.campsite.value[ this.form.campsite.valueKey ] : null,
                valid_from: this.form.valid_from.isHidden ? null : this.form.valid_from.value,
                valid_to: this.form.valid_to.isHidden ? null : this.form.valid_to.value,
                allotment: this.form.allotment.isHidden ? null : this.form.allotment.value,
            };

            if(data.campsite_id === -1) {
                data.campsite_id = null;
                data.accommodation_maintype = this.form.campsite.value['value'];
            }

            const response = await Api.portalMembers.create(data);

            if(response.status === 400) {
                EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                    title: `<h3>${this.$t('generic.formErrorHeadline')}</h3>`,
                    direction: 'Up',
                    type: 'danger',
                    body: response.body.non_field_errors[0],
                });
            }

            this.$emit('update-portal');
            this.toggleModal();
        },
    },
};
</script>

<style lang="scss" scoped>
.add-accommodation {
    .modal-card {
        width: 50%;
        max-width: 800px;
        min-height: 45rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/components/portals/addAccommodation.vue